/**
 * <EmailPreferences /> — public Lumfi-native email preference center.
 *
 * Two modes, picked from the URL:
 *
 *   1. `/email-preferences?token=<jwt>` — the JWT-authenticated mode.
 *      The token comes embedded in the "manage preferences" link of
 *      every marketing email we send. On mount the page calls
 *      `/api/email-preferences/resolve?token=...` to fetch the topic
 *      catalog + the user's current opt state, and renders one toggle
 *      per topic. Updates write through `PUT /api/email-preferences/update`.
 *
 *   2. `/email-preferences` (no token, expired, invalid) — the
 *      request-link fallback. The user types their email and we mail
 *      them a fresh JWT'd link. Same form as before.
 *
 * Resend is no longer in the loop UX-wise: Lumfi owns the page, the
 * topics, and the subscription state. The provider only sees the
 * resulting subscription updates (pushed by `topic_service`).
 */
import { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useLang } from "../contexts/LangContext";
import {
  Mail, ShieldCheck, Loader2, BellOff, CheckCircle2,
  Eye, Sparkles, MessageCircle,
} from "lucide-react";
import { useFormUX, FormField, SubmitButton } from "../components/form-ux";

const useTokenFromQuery = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get("token") || "").trim() || null;
};

export default function EmailPreferences() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const initialToken = useTokenFromQuery();
  const [token] = useState(initialToken);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-8 rounded-2xl border-slate-200" data-testid="email-preferences-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-emerald-700" />
          <h1 className="font-display text-xl font-bold text-slate-900">
            {isEs ? "Preferencias de email" : "Email preferences"}
          </h1>
        </div>
        {token ? (
          <TokenPanel token={token} isEs={isEs} />
        ) : (
          <RequestLinkPanel isEs={isEs} />
        )}
        <p className="text-[10px] text-slate-400 mt-6 text-center">
          {isEs
            ? "Tus preferencias se guardan en Lumfi (única fuente de verdad)."
            : "Your preferences are stored in Lumfi (single source of truth)."}
        </p>
      </Card>
    </div>
  );
}

function TokenPanel({ token, isEs }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [pendingId, setPendingId] = useState(null);
  const [unsubAllPending, setUnsubAllPending] = useState(false);

  // Resolve the token on mount. We pass the token as a query param —
  // it's a JWT, not a credential, and the endpoint is public by design.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/email-preferences/resolve`, { token });
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        if (!cancelled) {
          const reason = e?.response?.data?.detail || "invalid_token";
          setState({ loading: false, error: reason, data: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const update = async (topicId, subscription) => {
    setPendingId(topicId);
    try {
      await api.put("/email-preferences/update", { token, topic_id: topicId, subscription });
      // Optimistic local merge so the toggle reflects instantly.
      setState((s) => ({
        ...s,
        data: {
          ...s.data,
          topics: s.data.topics.map((t) => (t.id === topicId ? { ...t, subscription } : t)),
        },
      }));
      toast.success(isEs ? "Preferencia guardada" : "Preference saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEs ? "Error guardando" : "Save failed"));
    } finally {
      setPendingId(null);
    }
  };

  const unsubscribeAll = async () => {
    setUnsubAllPending(true);
    try {
      await api.post("/email-preferences/unsubscribe-all", { token });
      setState((s) => ({
        ...s,
        data: {
          ...s.data,
          topics: s.data.topics.map((t) => ({ ...t, subscription: "opt_out" })),
        },
      }));
      toast.success(isEs ? "Te dimos de baja de todos los temas" : "You're unsubscribed from everything");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEs ? "Error" : "Error"));
    } finally {
      setUnsubAllPending(false);
    }
  };

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center" data-testid="prefs-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        {isEs ? "Cargando tus preferencias…" : "Loading your preferences…"}
      </div>
    );
  }

  if (state.error) {
    return <ExpiredOrInvalidToken isEs={isEs} reason={state.error} />;
  }

  const { email, topics } = state.data;
  const allOptedOut = topics.every((t) => t.subscription === "opt_out");

  return (
    <div data-testid="prefs-authenticated-panel">
      <p className="text-xs text-slate-500 mt-2 mb-4">
        {isEs ? "Gestionando preferencias de" : "Managing preferences for"}{" "}
        <span className="font-mono text-slate-700">{email}</span>
      </p>
      {topics.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-400" data-testid="prefs-empty">
          {isEs ? "Sin categorías configuradas todavía." : "No categories configured yet."}
        </div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="prefs-topic-list">
          {topics.map((t) => (
            <PrefRow
              key={t.id}
              label={t.name}
              hint={t.description}
              checked={t.subscription !== "opt_out"}
              disabled={pendingId === t.id || unsubAllPending}
              onChange={(v) => update(t.id, v ? "opt_in" : "opt_out")}
              testid={`prefs-topic-${t.id}`}
            />
          ))}
        </div>
      )}
      {topics.length > 0 && !allOptedOut && (
        <div className="border-t border-slate-200 mt-4 pt-4 flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={unsubscribeAll}
            disabled={unsubAllPending}
            className="text-rose-700 border-rose-200 hover:bg-rose-50 gap-1"
            data-testid="prefs-unsubscribe-all"
          >
            {unsubAllPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="h-3 w-3" />}
            {isEs ? "Desuscribirme de todo" : "Unsubscribe from everything"}
          </Button>
        </div>
      )}
      {topics.length > 0 && allOptedOut && (
        <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center gap-2" data-testid="prefs-all-opted-out">
          <CheckCircle2 className="h-4 w-4 text-slate-500" />
          {isEs
            ? "Estás dado de baja de todas las categorías. Vuelve a habilitar las que quieras arriba."
            : "You're opted out of everything. Re-enable any topic above when you want it back."}
        </div>
      )}
      <DigestPreviewCard token={token} isEs={isEs} />
    </div>
  );
}

function DigestPreviewCard({ token, isEs }) {
  // iter-211 — Public, token-gated weekly digest preview. Renders the
  // exact body the Monday cron would push for this user RIGHT NOW.
  // No side effects (no digest_log row, no send).
  const [state, setState] = useState({ loading: false, preview: null, error: null });
  const [opened, setOpened] = useState(false);

  const load = async () => {
    setState({ loading: true, preview: null, error: null });
    try {
      const { data } = await api.post("/email-preferences/digest-preview", { token });
      setState({ loading: false, preview: data, error: null });
    } catch (e) {
      const reason = e?.response?.data?.detail || "preview_failed";
      setState({ loading: false, preview: null, error: reason });
    }
  };

  const handleOpen = () => {
    setOpened(true);
    void load();
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="digest-preview-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              {isEs ? "Vista previa del próximo digest" : "Next digest preview"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEs
                ? "Esto recibirás el próximo lunes a las 8pm UTC."
                : "This is what you'll get next Monday at 8pm UTC."}
            </p>
          </div>
        </div>
        {!opened && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpen}
            className="gap-1.5 flex-shrink-0"
            data-testid="digest-preview-open"
          >
            <Eye className="h-3.5 w-3.5" />
            {isEs ? "Ver vista previa" : "Show preview"}
          </Button>
        )}
        {opened && !state.loading && (
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="gap-1.5 flex-shrink-0"
            data-testid="digest-preview-refresh"
          >
            <Eye className="h-3.5 w-3.5" />
            {isEs ? "Refrescar" : "Refresh"}
          </Button>
        )}
      </div>

      {opened && state.loading && (
        <div
          className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center bg-slate-50 rounded-xl border border-slate-100"
          data-testid="digest-preview-loading"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {isEs ? "Computando tu digest…" : "Computing your digest…"}
        </div>
      )}

      {opened && state.error && (
        <div
          className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3"
          data-testid="digest-preview-error"
        >
          {isEs ? "No pudimos cargar la vista previa." : "Couldn't load the preview."}
        </div>
      )}

      {opened && state.preview && (
        <div
          className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white"
          data-testid="digest-preview-body"
        >
          {state.preview.has_content ? (
            <>
              <div className="flex items-center gap-2 px-4 py-2 border-b border-amber-100 text-[11px] text-amber-800 font-semibold uppercase tracking-wide">
                <Mail className="h-3 w-3" />
                {isEs ? "Email simulado" : "Simulated email"}
                <span className="ml-auto text-amber-600 font-mono normal-case tracking-normal">
                  {state.preview.week_label}
                </span>
              </div>
              <pre
                className="text-xs text-slate-900 whitespace-pre-wrap font-sans leading-relaxed p-4 max-w-[600px]"
                data-testid="digest-preview-text"
              >
                {state.preview.body}
              </pre>
              {state.preview.has_whatsapp && (
                <div
                  className="border-t border-amber-100 px-4 py-3 flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50/40"
                  data-testid="digest-preview-whatsapp-chip"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {isEs
                    ? "También recibirás esto por WhatsApp."
                    : "You'll also receive this on WhatsApp."}
                </div>
              )}
            </>
          ) : (
            <p
              className="text-xs text-slate-500 italic p-4"
              data-testid="digest-preview-empty"
            >
              {isEs
                ? "Aún no hay nada que resumir esta semana. Registra ingresos, gastos o pagos próximos para que tu digest tenga contenido."
                : "Nothing to digest yet this week. Add income, expenses or upcoming payments so your digest has content."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ExpiredOrInvalidToken({ isEs, reason }) {
  const isExpired = reason === "token_expired";
  return (
    <div className="mt-4 space-y-3" data-testid="prefs-token-invalid">
      <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
        {isExpired
          ? (isEs
              ? "Este link expiró. Pide uno nuevo abajo con el mismo email."
              : "This link has expired. Request a new one below with the same email.")
          : (isEs
              ? "Link inválido o ya usado. Pide uno nuevo."
              : "Invalid or already-used link. Request a new one.")}
      </div>
      <RequestLinkPanel isEs={isEs} />
    </div>
  );
}

function RequestLinkPanel({ isEs }) {
  // iter-169zze — Migrated to useFormUX for inline validation + auto-
  // disabled submit + a11y. The "we don't surface auth errors" rule
  // still applies: any failure lands on the same generic confirmation
  // so we don't leak which addresses are registered.
  const [done, setDone] = useState(false);
  const form = useFormUX(
    { email: "" },
    {
      validate: (v) => {
        const fieldErrors = {};
        const e = v.email.trim();
        if (!e) {
          fieldErrors.email = isEs ? "Ingresa tu email." : "Enter your email.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
          fieldErrors.email = isEs ? "Email inválido." : "Invalid email.";
        }
        return { fieldErrors };
      },
    },
  );

  const submit = form.handleSubmit(
    async (v) => {
      try {
        await api.post("/email-preferences/request-link", {
          email: v.email.trim().toLowerCase(),
        });
      } catch {
        // Intentional silent success — see note in module header.
      }
      setDone(true);
    },
  );

  return (
    <div data-testid="prefs-request-link-panel">
      <h2 className="text-sm font-semibold text-slate-700 mt-3 flex items-center gap-1.5">
        <Mail className="h-3.5 w-3.5" />
        {isEs ? "Recibí tu link de preferencias" : "Get your preference link"}
      </h2>
      <p className="text-xs text-slate-500 mt-1">
        {isEs
          ? "Ingresa tu email y te mandamos un link directo a tu página de preferencias."
          : "Enter your email and we'll mail you a direct link to your preference page."}
      </p>
      {done ? (
        <div
          data-testid="email-preferences-sent"
          className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-800"
        >
          {isEs
            ? "Si tu email está registrado, el link llega en los próximos minutos. Revisa tu bandeja y la carpeta de spam."
            : "If your email is registered, the link arrives within a few minutes. Check your inbox and spam folder."}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-3" data-testid="email-preferences-form" noValidate>
          <FormField
            label={isEs ? "Email" : "Email"}
            name="email"
            required
            error={form.touched.email || form.submitCount > 0 ? form.errors.email : undefined}
          >
            <FormField.Input
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="tu@email.com"
              data-testid="email-preferences-input"
              {...form.getFieldProps("email")}
            />
          </FormField>
          <SubmitButton
            submitting={form.submitting}
            loadingText={isEs ? "Enviando…" : "Sending…"}
            testid="email-preferences-submit"
            className="bg-emerald-600 hover:bg-emerald-700 w-full"
          >
            {isEs ? "Enviar link" : "Send link"}
          </SubmitButton>
        </form>
      )}
    </div>
  );
}

function PrefRow({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className="flex items-start justify-between gap-3 py-3 cursor-pointer group">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
          checked ? "bg-emerald-600 border-emerald-700" : "bg-slate-200 border-slate-300"
        } disabled:opacity-50`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}
